<template>
  <div>
    <h3 class="mt-3 mb-4 title primary--text">
      {{ $t('Supervisions') }}
    </h3>
    <v-row class="white">
      <v-col>
        <div class="d-flex align-center">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('Search')"
            single-line
            hide-details
          />
          <v-btn
            class="ml-4"
            color="secondary"
            @click="exportTable"
          >
            {{ $t('export') }}
          </v-btn>
        </div>

        <v-data-table
          :headers="headers"
          :items="recommendationFiltered"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :group-by.sync="groupBy"
        >
          <template v-slot:header.laboratory.name="{header}">
            {{ header.text }}
            <span
              class="mx-3"
              @click="groupBy = header.value"
            >
              {{ $t('Group') }}
            </span>
          </template>

          <template v-slot:header.supervision.type="{header}">
            {{ header.text }}
            <span
              class="mx-3"
              @click="groupBy = header.value"
            >
              {{ $t('Group') }}
            </span>
          </template>

          <template v-slot:body.prepend="data">
            <tr>
              <template v-for="header in data.headers">
                <td :key="header.value">
                  <v-autocomplete
                    v-if="header.value === 'status'"
                    v-model="statusFilter"
                    :items="recommendationStatuses"
                    dense
                    hide-details
                    outlined
                    color="blue-grey lighten-2"
                    :label="$t('Filter By status')"
                    cache-items
                    clearable
                    item-text="name"
                  />
                </td>
              </template>
            </tr>
          </template>

          <template v-slot:group.header="{group, groupBy, headers, isOpen, toggle, remove, items}">
            <td
              :colspan="headers.length"
              class="text-start"
            >
              <div class="d-flex">
                <div>
                  <v-btn
                    icon
                    small
                    @click="toggle"
                  >
                    <v-icon v-if="isOpen">
                      mdi-minus
                    </v-icon>
                    <v-icon v-else>
                      mdi-plus
                    </v-icon>
                  </v-btn>

                  {{ groupByNameMapper[groupBy] }}: {{ group }}
                  <v-btn
                    icon
                    small
                    @click="remove"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div
                  v-if="groupBy == 'laboratory.name'"
                  class="ml-auto"
                >
                  <v-btn
                    icon
                    :to="{name: 'SupervisionList', params: {laboratoryId: items[0].laboratory.id}}"
                    target="_blank"
                  >
                    <v-icon
                      small
                    >
                      mdi-open-in-new
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </td>
          </template>

          <template v-slot:item.createdAt="{ item }">
            <date-formatter :unix-time="item.createdAt" />
          </template>

          <template v-slot:item.deadline="{ item }">
            <span
              :class="{warning: !item.closedDate && item.deadline <= today}"
              class="py-1 px-2 rounded"
            >
              <date-formatter :unix-time="item.deadline" />
            </span>
          </template>

          <template v-slot:item.closedDate="{ item }">
            <span
              v-if="!!item.closedDate"
              :class="{warning: item.deadline < item.closedDate}"
              class="py-1 px-2 rounded"
            >
              <date-formatter :unix-time="item.closedDate" />
            </span>
            <span v-else>-</span>
          </template>

          <template v-slot:item.files="{ item }">
            <files
              :files="item.files"
              :file-link-path="getRecommendationFileLinkPath(item)"
              :can-remove="false"
            />
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              :to="{name: 'Supervision',params: {laboratoryId: item.laboratory.id, supervisionId: item.supervision.id}}"
              target="_blank"
            >
              <v-icon
                small
              >
                mdi-open-in-new
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>

    import DateFormatter from "@/components/atoms/DateFormatter";
    import moment from 'moment';
    import {
        export_json_to_excel_custom
    } from '@/vendor/Export2Excel'
    import Files from "@/components/common/Files";

    export default {
        name: "ReqToolRecommendation",

        components: {
            DateFormatter,
            Files
        },

        props: {
            supervisions: {
                type: Array,
                required: true,
            },
            dateRange: {
                type: Array,
                required: true,
            }
        },

        data: () => {
            return {
                search: '',
                statusFilter: '',
                sortDesc: false,
                sortBy: 'laboratory',
                groupByNameMapper: {
                    'laboratory.name': 'Laboratory Name',
                    'supervision.type': 'Supervision Type'
                },
                groupBy: []
            }
        },

        computed: {
            headers() {
                let headers = [
                    {
                        text: this.$t('Laboratory'),
                        value: 'laboratory.name',
                        align: 'start',
                        sortable: true,
                        groupable: true,
                    },
                    {
                        text: this.$t('Supervision Type'),
                        value: 'supervision.type',
                        align: 'start',
                        sortable: true,
                        groupable: true,
                    },
                    {
                        text: this.$t('Status'),
                        value: 'status',
                        align: 'start',
                        sortable: true,
                        groupable: false,
                    },
                    {
                        text: this.$t('CreatedAt'),
                        value: 'createdAt',
                        align: 'start',
                        sortable: true,
                        groupable: false,
                    },
                    {
                        text: this.$t('Deadline'),
                        value: 'deadline',
                        align: 'start',
                        sortable: true,
                        groupable: false,
                    },
                    {
                        text: this.$t('Closed Date'),
                        value: 'closedDate',
                        align: 'start',
                        sortable: true,
                        groupable: false,
                    },
                    {
                        text: this.$t('Recommendation'),
                        value: 'text',
                        align: 'start',
                        sortable: true,
                        groupable: false,
                    },
                    {
                        text: this.$t('Comments'),
                        value: 'comments',
                        align: 'start',
                        sortable: true,
                        groupable: false,
                    },
                    {
                        text: this.$t('Files'),
                        value: 'files',
                        align: 'start',
                        sortable: false,
                        groupable: false,
                    },
                    {
                        text: this.$t('Actions'),
                        value: 'actions',
                        align: 'end',
                        sortable: false,
                        groupable: false,
                    },
                ];

                return headers
            },

            supervisionsFiltered() {
                // if(this.dateRange.length) {
                //     return this.supervisions.filter(supervision => moment(supervision.createdAt).isBetween(this.dateRange[0], this.dateRange[1]))
                // }


                return this.supervisions
            },

            recommendations() {
                return this.supervisionsFiltered.map(supervision => {
                    supervision.recommendations.forEach(r => {
                        r.supervision = supervision;
                        r.laboratory = supervision.laboratory
                    });

                    return supervision.recommendations;
                }).flat();
            },

            recommendationFiltered() {
                let recommendations = this.recommendations;

                if (this.dateRange.length || this.statusFilter) {
                    recommendations = recommendations.filter(recommendation => {
                        if (this.statusFilter && this.statusFilter !== recommendation.status) {
                            return false
                        }

                        if(this.dateRange.length) {
                          return moment(recommendation.createdAt).isBetween(this.dateRange[0], this.dateRange[1], undefined, '[]')
                        }

                        return true
                    })
                }

                return recommendations
            },

            recommendationStatuses() {
                let statuses = this.recommendations.reduce((a, r) => {
                    a[r.status] = r.status;
                    return a;
                }, {});
                return Object.keys(statuses)
            },

            today() {
                return new Date(new Date().toISOString().substr(0, 10)).getTime()
            },
        },
        methods: {
            getRecommendationFileLinkPath(item) {
                return `/laboratory/${item.laboratory.id}/supervision/${item.supervision.id}/recommendation/${item.id}/files`
            },

            exportTable() {
                let multiData = [];

                {
                    const tHeader = [
                        '#', 'Laboratory', 'Supervision Type', 'Status', 'CreatedAt', 'Deadline', 'Closed Date', 'Recommendation', 'Comments'
                    ];


                    const filterVal = [
                        '#', 'laboratory', 'supervision', 'status', 'createdAt', 'deadline', 'closedDate', 'text', 'comments'
                    ];

                    const deepValues = {
                        laboratory(item) {
                            return item?.laboratory?.name || ''
                        },
                        supervision(item) {
                            return item?.supervision?.type || ''
                        },
                        createdAt(item) {
                            return moment(item.createdAt).format('YYYY/MM/DD')
                        },
                        deadline(item) {
                            return moment(item.createdAt).format('YYYY/MM/DD')
                        },
                        closedDate(item) {
                            return moment(item.createdAt).format('YYYY/MM/DD')
                        }
                    };


                    let data = this.recommendationFiltered.map((v, index) => {
                        return filterVal.map(j => {
                            if (j === '#') return index + 1;
                            if (v[j]) {
                                if (j in deepValues) {
                                    return deepValues[j](v)
                                } else {
                                    return v[j].toString()
                                }
                            }

                            return ''
                        });
                    }).filter(e => e);

                    multiData.push({name: 'Recommendation', header: tHeader, data})
                }

                export_json_to_excel_custom({
                    multiData,
                    filename: 'recommendation',
                    autoWidth: true,
                })

            }

        }

    }
</script>

<style scoped>

</style>
